/**
 * Afwezigheid en reactietijd (plak 2.3).
 *
 * Eén scherm met drie dingen: de twee schakelaars, wat er nu geldt, en
 * de droge meting van de afgelopen week.
 *
 * De volgorde op dit scherm is de volgorde waarin JP het in gebruik
 * neemt:
 *
 * 1. zet het afwezigheidsbericht **aan** terwijl de droge stand aan
 *    blijft staan — er gaat dan nog niets naar een klant;
 * 2. kijk een week naar de meting hieronder;
 * 3. zet de droge stand pas daarna uit.
 *
 * De schakelaars schrijven direct weg, via dezelfde
 * `PUT /api/werkplek/instellingen` als het beheerscherm. Er is geen
 * tweede waarheid: dit scherm is een andere blik op dezelfde twee
 * instellingen (les 8 september — een schakelaar die alleen het concept
 * in de browser wijzigt, laat een klantregel live staan).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { vertaalApiFout } from "@/lib/api";
import {
  werkplekAfwezigheidApi,
  werkplekApi,
  type AfwezigheidStand,
} from "@/lib/werkplek-api";

import "./afwezigheid.css";
import { MetCopiloot } from "../Copiloot/MetCopiloot";

const REDEN_TEKST: Record<string, string> = {
  weekend: "weekend of feestdag",
  avond: "buiten openingstijden",
  verlof: "verlof",
};

function momentTekst(iso: string | null): string {
  if (!iso) return "onbekend";
  return new Date(iso).toLocaleString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function datumTekst(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
  });
}

export function WerkplekAfwezigheid() {
  const queryClient = useQueryClient();

  const stand = useQuery({
    queryKey: ["werkplek", "afwezigheid"],
    queryFn: werkplekAfwezigheidApi.stand,
  });

  const zetten = useMutation({
    mutationFn: ({ sleutel, waarde }: { sleutel: string; waarde: boolean }) =>
      werkplekApi.zetInstelling(sleutel, waarde),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["werkplek"] });
      toast.success("Opgeslagen.");
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  const proefronde = useMutation({
    mutationFn: werkplekAfwezigheidApi.proefronde,
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  const data: AfwezigheidStand | undefined = stand.data;

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>
          Afwezigheid en reactietijd
        </h1>
      </div>
      <div className="wp-inhoud">
        <MetCopiloot pagina="beheer">
        <div style={{ overflowY: "auto", padding: 18, maxWidth: 820 }}>
          {stand.isLoading ? (
            <p style={{ color: "var(--wp-tekst2)" }}>Bezig met laden…</p>
          ) : stand.isError || !data ? (
            <p style={{ color: "var(--wp-rood)" }}>
              De stand is niet op te halen. Alleen een beheerder mag deze
              pagina zien.
            </p>
          ) : (
            <>
              {data.blokkade ? (
                <p
                  role="alert"
                  style={{
                    background: "var(--wp-rood-licht)",
                    border: "1px solid var(--wp-rood)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 13,
                  }}
                >
                  {data.blokkade}
                </p>
              ) : null}

              <div className="wp-blok">
                <h4>De twee schakelaars</h4>
                <Schakelaar
                  label="Afwezigheidsbericht"
                  uitleg={
                    "Aan: buiten kantoortijd krijgt een klant die Sanne niet " +
                    "zelf kan helpen een ontvangstbevestiging met de " +
                    "verwachte reactietijd. Hoogstens een keer per klant per " +
                    "venster."
                  }
                  aan={data.actief}
                  bezig={zetten.isPending}
                  onZet={(waarde) =>
                    zetten.mutate({ sleutel: "afwezigheid_actief", waarde })
                  }
                />
                <Schakelaar
                  label="Droge stand"
                  uitleg={
                    "Aan: er wordt alleen gelogd wat er zou zijn gegaan, en " +
                    "er gaat niets naar een klant. Zet hem pas uit als je " +
                    "een week aan de meting hieronder hebt gezien wat er " +
                    "zou zijn uitgegaan."
                  }
                  aan={data.droog}
                  bezig={zetten.isPending}
                  waarschuwing={
                    data.droog
                      ? undefined
                      : "Deze staat uit: berichten gaan nu echt naar klanten."
                  }
                  onZet={(waarde) =>
                    zetten.mutate({ sleutel: "afwezigheid_droog", waarde })
                  }
                />
              </div>

              <div className="wp-blok">
                <h4>Wat er nu geldt</h4>
                <dl className="wp-afwezig-lijst">
                  <dt>Nu</dt>
                  <dd>
                    {data.nu_afwezig
                      ? `We zijn er niet (${
                          REDEN_TEKST[data.reden ?? ""] ?? data.reden
                        })`
                      : "Er wordt nu gewerkt"}
                  </dd>
                  {data.nu_afwezig ? (
                    <>
                      <dt>Weer bereikbaar</dt>
                      <dd>{momentTekst(data.venster_einde)}</dd>
                      <dt>Belofte in het bericht</dt>
                      <dd>
                        antwoord uiterlijk {momentTekst(data.reactie_op)}
                      </dd>
                    </>
                  ) : null}
                  <dt>Werkdag</dt>
                  <dd>
                    {String(data.avond_tot_uur).padStart(2, "0")}:00 tot{" "}
                    {String(data.avond_van_uur).padStart(2, "0")}:00, maandag
                    tot en met vrijdag. Weekend en Nederlandse feestdagen
                    tellen niet mee.
                  </dd>
                  <dt>Reactiebelofte</dt>
                  <dd>{data.reactie_werkuren} werkuren na terugkomst</dd>
                  <dt>Verlofdagen</dt>
                  <dd>
                    {data.verlofdagen.length === 0
                      ? "geen"
                      : data.verlofdagen.join(", ")}
                  </dd>
                </dl>
              </div>

              <div className="wp-blok">
                <h4>De droge meting</h4>
                <p style={{ fontSize: 13, color: "var(--wp-tekst2)" }}>
                  Van {datumTekst(data.week_vanaf)} tot{" "}
                  {datumTekst(data.week_tot)}.
                </p>
                <p style={{ fontSize: 15 }}>
                  <strong>{data.week_droog}</strong> bericht(en) zouden er zijn
                  gegaan (droog gelogd, niet verstuurd) en{" "}
                  <strong>{data.week_verstuurd}</strong> zijn er echt
                  klaargezet.
                </p>

                <button
                  type="button"
                  className="wp-knop"
                  disabled={proefronde.isPending}
                  onClick={() => proefronde.mutate()}
                >
                  Kijk wat er nu zou gebeuren
                </button>
                <p style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
                  Deze knop verstuurt nooit iets en schrijft niets weg, ook
                  niet als de droge stand uit staat.
                </p>

                {proefronde.data ? (
                  <Proefuitslag uitslag={proefronde.data} />
                ) : null}
              </div>
            </>
          )}
        </div>
      </MetCopiloot>
      </div>
    </>
  );
}

function Schakelaar({
  label,
  uitleg,
  aan,
  bezig,
  waarschuwing,
  onZet,
}: {
  label: string;
  uitleg: string;
  aan: boolean;
  bezig: boolean;
  waarschuwing?: string;
  onZet: (waarde: boolean) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        padding: "12px 14px",
        borderBottom: "1px solid var(--wp-lijn2)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>{uitleg}</div>
        {waarschuwing ? (
          <div style={{ fontSize: 12, color: "var(--wp-rood)", marginTop: 4 }}>
            {waarschuwing}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="wp-knop"
        disabled={bezig}
        aria-pressed={aan}
        onClick={() => onZet(!aan)}
        style={
          aan
            ? {
                background: "var(--wp-groen)",
                borderColor: "var(--wp-groen)",
                color: "#fff",
              }
            : undefined
        }
      >
        {aan ? "Staat aan" : "Staat uit"}
      </button>
    </div>
  );
}

function Proefuitslag({
  uitslag,
}: {
  uitslag: {
    overgeslagen: string | null;
    bekeken: number;
    droog_gelogd: number;
    redenen: Record<string, number>;
  };
}) {
  if (uitslag.overgeslagen) {
    return (
      <p style={{ fontSize: 13 }} role="status">
        Er zou nu niets gebeuren: {uitslag.overgeslagen}.
      </p>
    );
  }
  const redenen = Object.entries(uitslag.redenen);
  return (
    <div role="status">
      <p style={{ fontSize: 13 }}>
        {uitslag.bekeken} wachtende mail(s) bekeken;{" "}
        <strong>{uitslag.droog_gelogd}</strong> klant(en) zouden nu een
        afwezigheidsbericht krijgen.
      </p>
      {redenen.length > 0 ? (
        <>
          <p style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
            Overgeslagen, en waarom:
          </p>
          <ul style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
            {redenen.map(([reden, aantal]) => (
              <li key={reden}>
                {aantal}× {reden}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
